import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { useMemo, useState } from 'react'

import { EmptyValue } from '@/components/EmptyValue'
import { PageHeader } from '@/components/PageHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  type AdminEmailLogRow,
  getEmailLogEntry,
  listEmailLog,
} from '@/lib/admin/server'
import { AdminListCard } from './-components/AdminListCard'

export const Route = createFileRoute('/_authenticated/admin/emails')({
  component: AdminEmailsPage,
})

function AdminEmailsPage() {
  const [viewing, setViewing] = useState<AdminEmailLogRow | null>(null)

  const columns = useMemo<ColumnDef<AdminEmailLogRow>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        header: 'Sent',
        enableSorting: true,
        cell: ({ row }) => (
          <div className="flex flex-col gap-0.5 whitespace-nowrap">
            <span>{new Date(row.original.createdAt).toLocaleDateString()}</span>
            <span className="text-xs text-muted-foreground">
              {new Date(row.original.createdAt).toLocaleTimeString()}
            </span>
          </div>
        ),
      },
      {
        accessorKey: 'type',
        header: 'Template',
        enableSorting: true,
        cell: ({ row }) => (
          <Badge variant="secondary">{row.original.type}</Badge>
        ),
      },
      {
        accessorKey: 'recipient',
        header: 'Recipient',
        enableSorting: true,
        cell: ({ row }) => (
          <span className="font-medium">{row.original.recipient}</span>
        ),
      },
      {
        accessorKey: 'subject',
        header: 'Subject',
        cell: ({ row }) => (
          <span className="block max-w-96 truncate text-muted-foreground">
            {row.original.subject}
          </span>
        ),
      },
      {
        accessorKey: 'organizationName',
        header: 'Organization',
        enableSorting: true,
        cell: ({ row }) =>
          row.original.organizationId ? (
            <Link
              to="/admin/organizations/$orgId"
              params={{ orgId: row.original.organizationId }}
              className="hover:underline"
            >
              {row.original.organizationName ?? row.original.organizationId}
            </Link>
          ) : (
            <EmptyValue>None</EmptyValue>
          ),
      },
      {
        accessorKey: 'outcome',
        header: 'Outcome',
        enableSorting: true,
        cell: ({ row }) =>
          row.original.outcome === 'sent' ? (
            <Badge variant="secondary">Sent</Badge>
          ) : (
            <div className="flex flex-col gap-0.5">
              <Badge variant="destructive">Failed</Badge>
              {row.original.error ? (
                <span className="max-w-64 truncate text-xs text-muted-foreground">
                  {row.original.error}
                </span>
              ) : null}
            </div>
          ),
      },
      {
        id: 'actions',
        cell: ({ row }) => (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setViewing(row.original)}
          >
            View
          </Button>
        ),
      },
    ],
    [],
  )

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Emails"
        description="Every transactional email the platform attempted to send, with the exact content."
      />
      <AdminListCard
        title="Email log"
        noun="email"
        queryKey={['admin-email-log']}
        queryFn={() => listEmailLog()}
        columns={columns}
        emptyMessage="No emails logged yet."
        searchAccessor={(e) =>
          `${e.recipient} ${e.subject} ${e.type} ${e.organizationName ?? ''}`
        }
        searchPlaceholder="Search by recipient, subject, or template"
      />
      <EmailViewerDialog email={viewing} onClose={() => setViewing(null)} />
    </div>
  )
}

/** The stored payload, exactly as handed to SMTP: rendered HTML in a sandboxed
 *  iframe (no scripts, no network beyond the doc itself) plus the plain-text
 *  alternative. */
function EmailViewerDialog({
  email,
  onClose,
}: {
  email: AdminEmailLogRow | null
  onClose: () => void
}) {
  const { data, isPending } = useQuery({
    queryKey: ['admin-email-log-entry', email?.id],
    queryFn: () => getEmailLogEntry({ data: { id: email?.id ?? '' } }),
    enabled: !!email,
  })

  return (
    <Dialog open={!!email} onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{email?.subject}</DialogTitle>
          <DialogDescription>
            {email
              ? `${email.type} to ${email.recipient} on ${new Date(email.createdAt).toLocaleString()}`
              : ''}
          </DialogDescription>
        </DialogHeader>
        {isPending ? (
          <Skeleton className="h-96 w-full" />
        ) : data?.html ? (
          <Tabs defaultValue="html">
            <TabsList>
              <TabsTrigger value="html">Rendered</TabsTrigger>
              <TabsTrigger value="text">Plain text</TabsTrigger>
            </TabsList>
            <TabsContent value="html">
              <iframe
                title="Email content"
                // Scripts stay blocked; same-origin is needed so the wordmark
                // image (served from our own /email path) loads inside the
                // opaque-origin srcdoc document.
                sandbox="allow-same-origin"
                srcDoc={data.html}
                className="h-96 w-full rounded-md border bg-white"
              />
            </TabsContent>
            <TabsContent value="text">
              <pre className="h-96 w-full overflow-auto whitespace-pre-wrap rounded-md border p-3 text-sm">
                {data.text}
              </pre>
            </TabsContent>
          </Tabs>
        ) : (
          <pre className="h-96 w-full overflow-auto whitespace-pre-wrap rounded-md border p-3 text-sm">
            {data?.text}
          </pre>
        )}
      </DialogContent>
    </Dialog>
  )
}
